import { ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import './DataTable.css';

type DataTableType = {
  itemOptions?: number[];
  header?: {
    name: string;
    sortingAllowed: boolean;
    type: string;
    width?: string | number; // optional width
  }[];
  data?: any[];
};

export default function DataTable({
  itemOptions = [10],
  header = [{ name: '', sortingAllowed: true, type: '', width: 'auto' }],
  data = []
}: DataTableType) {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemsPerPage] = useState(itemOptions[0]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    null
  );

  const totalPages = Math.ceil(data.length / itemPerPage);

  const visibileTableContent = useMemo(() => {
    return data.slice(itemPerPage * (pageNumber - 1), pageNumber * itemPerPage);
  }, [data, itemPerPage, pageNumber]);

  function convertValue(value: any) {
    if (value === '-' || value === null || value === undefined) return Number.NEGATIVE_INFINITY;
    if (typeof value === 'string') {
      const cleaned = value.replace('€', '').replace('K', '000').replace('%', '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? cleaned.toLowerCase() : num;
    }
    return value;
  }

  function sortData(data: { [key: string]: any }[], key: string, direction: 'asc' | 'desc') {
    return [...data].sort((a, b) => {
      const valA = convertValue(a[key]);
      const valB = convertValue(b[key]);

      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const tableRows = useMemo(() => {
    if (sortConfig) {
      return sortData(visibileTableContent, sortConfig.key, sortConfig.direction);
    }
    return visibileTableContent;
  }, [sortConfig, visibileTableContent]);

  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      setSortConfig({ key, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <table
        className="table"
        style={{
          tableLayout: 'fixed'
        }}>
        <thead>
          <tr>
            {header.map((hdr) => (
              <td
                key={hdr.type}
                className={hdr.className}
                scope="col"
                style={{
                  height: '64px',
                  background: 'rgba(235, 242, 254, 1)',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  width: '100px',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }}>
                {hdr.name}
                {hdr.sortingAllowed && (
                  <ArrowDown
                    onClick={() => handleSort(hdr.type)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      transform:
                        sortConfig?.key === hdr.type && sortConfig.direction === 'desc'
                          ? 'rotate(180deg)'
                          : ''
                    }}
                  />
                )}
              </td>
            ))}
          </tr>
        </thead>
        <tbody style={{ maxHeight: '288px', width: '100%', backgroundColor: 'Red' }}>
          {tableRows.map((rw, rowIndex) => (
            <tr
              key={rowIndex}
              style={{
                height: '55px',
                verticalAlign: 'middle',
                borderBottom: '1px solid rgba(191, 215, 251, 1)',
                width: '100%'
              }}>
              {header.map((item) => (
                <td
                  key={item.type}
                  style={{
                    // width: item.width ?? 'auto',
                    width: '500px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                  {rw?.[item.type] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot style={{ width: '100%' }}>
          <tr style={{ height: '64px' }}>
            <td
              colSpan={header.length}
              style={{
                verticalAlign: 'middle',
                backgroundColor: 'rgba(235, 242, 254, 1)',
                width: '200px'
              }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: 'rgba(113, 113, 122, 1)' }}>Total: </span>{' '}
                  {/* {tableRows.length}  */}
                  {data.length}
                </div>
                <div>
                  <ChevronLeft
                    style={{
                      cursor: 'pointer',
                      color: pageNumber === 1 ? 'rgba(113, 113, 122, 1)' : 'var(--uni_std_clr)',
                      width: '16px'
                    }}
                    onClick={() => pageNumber !== 1 && setPageNumber(pageNumber - 1)}
                  />
                  <span
                    style={{
                      width: '50px',
                      borderBottom: '1px solid var(--uni_std_clr)',
                      color: 'var(--uni_std_clr)',
                      margin: '0 20px',
                      padding: '0 10px'
                    }}>
                    {pageNumber}
                  </span>
                  of{' '}
                  <span
                    style={{
                      width: '50px',
                      margin: '0 20px',
                      padding: '0 10px'
                    }}>
                    {totalPages}
                  </span>
                  <ChevronRight
                    style={{
                      cursor: 'pointer',
                      color:
                        pageNumber === totalPages ? 'rgba(113, 113, 122, 1)' : 'var(--uni_std_clr)'
                    }}
                    onClick={() => pageNumber !== totalPages && setPageNumber(pageNumber + 1)}
                  />
                </div>
                <div>
                  <form>
                    <label htmlFor="pages" style={{ color: 'rgba(113, 113, 122, 1)' }}>
                      Show Per Page:
                    </label>
                    <select
                      name="pages"
                      id="pages"
                      style={{
                        border: 'none',
                        backgroundColor: 'rgba(235, 242, 254, 1)',
                        cursor: 'pointer'
                      }}
                      onChange={(e) => setItemsPerPage(parseInt(e.target.value))}>
                      {itemOptions.map((pageItems, index) => (
                        <option value={pageItems} key={pageItems + index}>
                          {pageItems}
                        </option>
                      ))}
                    </select>
                  </form>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
